import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { BotoesPresenca } from "@/components/agenda/BotoesPresenca";
import { BotaoAtualizar } from "@/components/casca/BotaoAtualizar";
import { CabecalhoPagina, Tela } from "@/components/casca/CabecalhoPagina";
import { Vazio } from "@/components/casca/Vazio";
import { formatarTelefone } from "@/lib/painel/telefone";
import type { ItemAgenda } from "@/lib/telas/agenda";
import { agendaQuery } from "../consultas";

const DIAS_DA_FAIXA = 7;

/**
 * Os próximos 14 dias, e a presença de quem já passou — que é o que fecha o funil.
 *
 * Em cima, a semana numa faixa (tocar num dia mostra só ele) e quantos horários
 * já passaram sem ninguém marcar presença: esse número existia no servidor e
 * não aparecia, e é ele que deixa o custo por comparecimento errado.
 */
export function Agenda() {
  const { data: a } = useSuspenseQuery(agendaQuery());
  const [dia, setDia] = useState<string | null>(null);
  const [profissional, setProfissional] = useState("");

  const profissionais = [...new Set(a.dias.flatMap((d) => d.itens.map((i) => i.profissional)).filter((p) => p && p !== "—"))].sort();
  const dias = a.dias
    .filter((d) => !dia || d.chave === dia)
    .map((d) => ({ ...d, itens: d.itens.filter((i) => !profissional || i.profissional === profissional) }))
    .filter((d) => d.itens.length > 0);
  const quantos = a.dias.reduce((n, d) => n + d.total, 0);

  return (
    <Tela>
      <CabecalhoPagina
        titulo="Agenda"
        descricao={
          <>
            Os horários que o assistente marcou, lidos {a.origem === "google" ? "do Google Agenda da clínica" : "do banco de dados"}. Depois da
            hora, marque quem apareceu: é isso que fecha o funil.
          </>
        }
        acao={
          <>
            {profissionais.length > 1 && (
              <select
                value={profissional}
                onChange={(e) => setProfissional(e.target.value)}
                aria-label="Profissional"
                className="h-9 rounded-full border-0 bg-[var(--color-cartao)] px-3.5 text-[13px] font-semibold text-[var(--color-tinta)] shadow-botao outline-none"
              >
                <option value="">Todos os profissionais</option>
                {profissionais.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            )}
            <BotaoAtualizar />
          </>
        }
      />

      {quantos === 0 ? (
        <Vazio icone="agenda" titulo={`Nenhum horário nos próximos ${a.quantosDias} dias`}>
          Quando o assistente marcar alguém, aparece aqui — e depois da hora você marca quem apareceu, que é o que fecha o funil.
        </Vazio>
      ) : (
        <div className="flex flex-col gap-6">
          <FaixaDaSemana porDia={new Map(a.dias.map((d) => [d.chave, d.total]))} escolhido={dia} escolher={(k) => setDia(dia === k ? null : k)} />

          <div className="flex flex-wrap gap-2">
            <span className="flex h-6 items-center rounded-full bg-[var(--color-painel)] px-2.5 text-xs font-semibold text-[var(--color-suave)] shadow-[inset_0_0_0_1px_var(--color-borda)]">
              {quantos} {quantos === 1 ? "horário" : "horários"} nos próximos {a.quantosDias} dias
            </span>
            {a.semMarcar > 0 && (
              <span className="flex h-6 items-center gap-1.5 rounded-full bg-[var(--color-alerta-fundo)] px-2.5 text-xs font-semibold text-[var(--color-alerta)]">
                <span className="h-1.5 w-1.5 rounded-full bg-current" />
                {a.semMarcar === 1 ? "1 horário já passou sem presença marcada" : `${a.semMarcar} horários já passaram sem presença marcada`}
              </span>
            )}
            {dia && (
              <button
                type="button"
                onClick={() => setDia(null)}
                className="flex h-6 items-center rounded-full px-2.5 text-xs font-semibold text-[var(--color-marca-forte)] hover:bg-[var(--color-realce)]"
              >
                Ver todos os dias
              </button>
            )}
          </div>

          {dias.length === 0 && (
            <p className="py-8 text-center text-sm text-[var(--color-suave)]">Nenhum horário com esse filtro.</p>
          )}

          {dias.map((d) => (
            <section key={d.chave} className="flex flex-col gap-2">
              <h2 className="flex items-baseline gap-2">
                <span className="text-[17px] font-bold">{d.rotulo.charAt(0).toUpperCase() + d.rotulo.slice(1)}</span>
                <span className="text-sm text-[var(--color-fraco)]">
                  {d.itens.length} {d.itens.length === 1 ? "horário" : "horários"}
                </span>
              </h2>
              {d.itens.map((i) => (
                <Horario key={i.id} i={i} />
              ))}
            </section>
          ))}
        </div>
      )}
    </Tela>
  );
}

/** Um horário: a hora numa coluna, quem e o quê ao lado, e a presença à direita quando já passou. */
function Horario({ i }: { i: ItemAgenda }) {
  const status = !i.jaPassou
    ? { texto: "Marcado", cor: "bg-[var(--color-info-fundo)] text-[var(--color-info)]" }
    : i.presenca === "compareceu"
      ? { texto: "Compareceu", cor: "bg-[var(--color-realce)] text-[var(--color-marca-forte)]" }
      : i.presenca === "faltou"
        ? { texto: "Faltou", cor: "bg-[var(--color-atencao-fundo)] text-[var(--color-atencao)]" }
        : { texto: "Marque a presença", cor: "bg-[var(--color-alerta-fundo)] text-[var(--color-alerta)]" };

  return (
    <article className="flex overflow-hidden rounded-[14px] bg-[var(--color-cartao)] shadow-cartao">
      <div className="flex w-[76px] shrink-0 flex-col justify-center border-r border-[var(--color-borda)] px-3 py-3 md:w-24 md:px-4">
        <time className={"text-base font-bold " + (i.jaPassou ? "text-[var(--color-suave)]" : "")}>{i.hora}</time>
        <span className="num text-[12.5px] text-[var(--color-fraco)]">até {i.ate}</span>
      </div>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-4 gap-y-2.5 px-4 py-3">
        <div className="min-w-[200px] flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {i.telefone ? (
              <Link
                to="/conversas/$telefone"
                params={{ telefone: i.telefone }}
                className="text-[15px] font-bold text-[var(--color-tinta)] hover:text-[var(--color-marca-forte)] hover:underline"
              >
                {i.cliente}
              </Link>
            ) : (
              <span className="text-[15px] font-bold">{i.cliente}</span>
            )}
            <span className={"flex h-[22px] items-center rounded-full px-2.5 text-xs font-semibold " + status.cor}>{status.texto}</span>
          </div>
          <p className="mt-0.5 text-[13px] text-[var(--color-suave)]">
            {i.servico}
            {i.profissional && i.profissional !== "—" ? ` · ${i.profissional}` : ""}
            {i.unidade ? ` · ${i.unidade}` : ""}
            {i.telefone ? (
              <>
                {" · "}
                <span className="font-mono text-xs">{formatarTelefone(i.telefone)}</span>
              </>
            ) : null}
          </p>
        </div>
        {i.jaPassou && <BotoesPresenca eventoId={i.id} presenca={i.presenca} />}
      </div>
    </article>
  );
}

/** Os próximos sete dias, com quantos horários cada um tem. Tocar escolhe o dia; tocar de novo volta a mostrar todos. */
function FaixaDaSemana({ porDia, escolhido, escolher }: { porDia: Map<string, number>; escolhido: string | null; escolher: (k: string) => void }) {
  const hoje = new Date();
  const dias = Array.from({ length: DIAS_DA_FAIXA }, (_, n) => {
    const d = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() + n);
    const chave = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return { chave, semana: d.toLocaleDateString("pt-BR", { weekday: "short" }).replace(".", ""), numero: String(d.getDate()).padStart(2, "0"), total: porDia.get(chave) ?? 0 };
  });
  return (
    <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1" role="group" aria-label="Escolher um dia">
      {dias.map((d) => {
        const ativo = escolhido === d.chave;
        return (
          <button
            key={d.chave}
            type="button"
            onClick={() => escolher(d.chave)}
            disabled={d.total === 0}
            aria-pressed={ativo}
            className={
              "flex min-w-[68px] flex-col items-center rounded-[14px] px-2.5 py-2 transition-colors disabled:cursor-default " +
              (ativo
                ? "bg-[var(--color-marca)] text-[var(--color-sobre-marca)]"
                : "bg-[var(--color-cartao)] shadow-cartao enabled:hover:bg-[var(--color-painel)]")
            }
          >
            <small className={"text-[11.5px] font-semibold uppercase " + (ativo ? "opacity-85" : "text-[var(--color-fraco)]")}>{d.semana}</small>
            <b className="text-lg leading-tight">{d.numero}</b>
            <span className={"text-[11.5px] " + (ativo ? "opacity-85" : d.total ? "text-[var(--color-suave)]" : "text-[var(--color-fraco)]")}>
              {d.total === 0 ? "livre" : d.total === 1 ? "1 horário" : `${d.total} horários`}
            </span>
          </button>
        );
      })}
    </div>
  );
}
